/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { TQUtils } from "../utils/TQUtils";
import TQFont, { tqtypes } from "../public/TQTypes";
import TQTaskScheduler from "./TQTaskScheduler";

export class TQCollabFontsHandler {
    private _scheduler: TQTaskScheduler;

    constructor(scheduler: TQTaskScheduler) {
        this._scheduler = scheduler;
    }

    public async downloadCollabFont(context: tqtypes.FontCollaborationInternalContext) {
        let error: tqtypes.Error = context.error;
        if (context.downloadUrl) {
            const callback: tqtypes.GenericContext<tqtypes.InternalContext> = {
                root: {
                    downloadCB: null as any,
                    fontsList: [context.fontInfo.fontId],
                    fontsIds: [],
                    fontInfo: context.fontInfo,
                    unicodesList: [],
                },
                retryCount: 1,
            };
            error = await this._scheduler.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callback, context.downloadUrl);
        } else {
            error.errCode = tqtypes.ErrorCode.FONT_NOT_FOUND;
        }
        if (false == (await TQUtils.checkIfValidFontStream(context.fontInfo.fontBuffer))) {
            // ...
            context.fontInfo.fontBuffer = TQUtils.reversePartArray(context.fontInfo.fontBuffer, 0, 1040);
        }
        if (error.errCode == tqtypes.ErrorCode.NONE && context.fontInfo.fontBuffer.length) {
            this._scheduler.foundFontsHandler.putFontBufferInCache(context.fontInfo, false);
        }
        return error;
    }

    private _collabAPIResponseParser(responseBody: any, error: tqtypes.Error, _callback: tqtypes.GenericContext<tqtypes.CollaborationAuthInternalContext>) {
        const res = new Map<string, tqtypes.FontCollaborationInternalContext>();
        const callback = _callback as tqtypes.GenericContext<tqtypes.CollaborationAuthInternalContext>;
        if (callback.root.psNamesInfo == undefined) {
            callback.root.psNamesInfo = new Map<string, TQFont>();
        }
        if (callback.root.fontIDsInfo == undefined) {
            callback.root.fontIDsInfo = new Map<string, TQFont>();
        }
        const fontsInfo = new Map([...callback.root.psNamesInfo.entries(), ...callback.root.fontIDsInfo.entries()]);
        if (error.errCode != tqtypes.ErrorCode.NONE) {
            for (const [key, value] of fontsInfo) {
                const entry: tqtypes.FontCollaborationInternalContext = {
                    fontInfo: value,
                    error: error,
                    downloadUrl: "",
                };
                res.set(key, entry);
            }
        } else {
            const results: any[] = responseBody["results"];
            for (const key in results) {
                const font = fontsInfo.get(key);
                if (font != undefined) {
                    if (results[key]["expires_at"]) {
                        font.expiryTime = results[key]["expires_at"];
                    }
                    if (results[key]["source"] == "byof") {
                        font.type = tqtypes.FontType.BYOF;
                    }
                    if (font.familyName == "" && results[key]["family_name"]) {
                        font.familyName = results[key]["family_name"];
                    }
                    if (font.styleName == "" && results[key]["subfamily_name"]) {
                        font.styleName = results[key]["subfamily_name"];
                    }
                    if (font.fontName == "") {
                        font.fontName = font.familyName + " " + font.styleName;
                    }
                    if (font.postscriptName == "" && results[key]["postscript_name"]) {
                        font.postscriptName = results[key]["postscript_name"];
                    }
                    if (font.fontId == "" && results[key]["id"]) {
                        font.fontId = results[key]["id"];
                    }
                    const cachedFont = this._scheduler.cachedFonts().get(font.fontId);
                    if (cachedFont && (font.type == tqtypes.FontType.BYOF || cachedFont.type == tqtypes.FontType.BYOF)) {
                        // His own BYOF font
                        font.accessLevel = tqtypes.AccessLevel.READ_WRITE;
                    }
                    let downloadUrl = undefined;
                    if (results[key]["download_url"]) {
                        downloadUrl = results[key]["download_url"];
                    }
                    const fontErr = new tqtypes.Error();
                    if ((downloadUrl == undefined || downloadUrl == "") && error.errCode == tqtypes.ErrorCode.NONE) {
                        fontErr.errCode = tqtypes.ErrorCode.FONT_NOT_FOUND;
                        fontErr.errMsg = "FONT_NOT_FOUND";
                    }
                    const entry: tqtypes.FontCollaborationInternalContext = {
                        fontInfo: font,
                        error: fontErr,
                        downloadUrl: downloadUrl,
                    };
                    res.set(key, entry);

                    if (font.fontId != "" && font.postscriptName != "") {
                        this._scheduler.collabFonts.set(font.fontId, font);
                        this._scheduler.collabFonts.set(font.postscriptName, font);
                    }
                }
            }
        }
        return res;
    }

    public async getFontMetadataUsingCollabAuthAPI(
        psNames: Map<string, TQFont>,
        fontIds: Map<string, TQFont>,
        fontAccessToken: string,
        documentId: string
    ): Promise<Map<string, tqtypes.FontCollaborationInternalContext>> {
        const perChunk = 30; // max 30 items per chunk
        const psNamesArrayOfChunks: Map<string, TQFont>[] = [];
        const fontIdsArrayOfChunks: Map<string, TQFont>[] = [];
        if (psNames.size) {
            let index = 0;
            for (const [key, value] of psNames) {
                const chunkIndex = Math.floor(index / perChunk);
                if (!psNamesArrayOfChunks[chunkIndex]) {
                    psNamesArrayOfChunks[chunkIndex] = new Map<string, TQFont>(); // start a new chunk
                }
                psNamesArrayOfChunks[chunkIndex].set(key, value);
                index++;
            }
        }

        if (fontIds.size) {
            let index = 0;
            for (const [key, value] of fontIds) {
                const chunkIndex = Math.floor(index / perChunk);
                if (!fontIdsArrayOfChunks[chunkIndex]) {
                    fontIdsArrayOfChunks[chunkIndex] = new Map<string, TQFont>(); // start a new chunk
                }
                fontIdsArrayOfChunks[chunkIndex].set(key, value);
                index++;
            }
        }

        let fontIdsIndex = 0;
        const results = new Map<string, tqtypes.FontCollaborationInternalContext>();
        for (let index = 0; index < psNamesArrayOfChunks.length; index++) {
            const callback: tqtypes.GenericContext<tqtypes.CollaborationAuthInternalContext> = {
                root: {
                    psNames: Array.from(psNamesArrayOfChunks[index].keys()),
                    fontIDs: fontIdsIndex < fontIdsArrayOfChunks.length ? Array.from(fontIdsArrayOfChunks[fontIdsIndex].keys()) : [],
                    fontAccessToken: fontAccessToken,
                    documentId: documentId,
                    psNamesInfo: psNamesArrayOfChunks[index],
                    fontIDsInfo: fontIdsIndex < fontIdsArrayOfChunks.length ? fontIdsArrayOfChunks[fontIdsIndex] : new Map<string, TQFont>(),
                },
                retryCount: 3,
            };

            const { responsebody: responsebody, err: error, callback: _callback } = await this._scheduler.scheduleTask(tqtypes.TaskType.COLLAB_AUTH_API, callback);
            const res = this._collabAPIResponseParser(responsebody, error, _callback);
            for (const [key, value] of res) {
                results.set(key, value);
            }
            fontIdsIndex++;
        }
        for (; fontIdsIndex < fontIdsArrayOfChunks.length; fontIdsIndex++) {
            const callback: tqtypes.GenericContext<tqtypes.CollaborationAuthInternalContext> = {
                root: {
                    psNames: [],
                    fontIDs: Array.from(fontIdsArrayOfChunks[fontIdsIndex].keys()),
                    fontAccessToken: fontAccessToken,
                    documentId: documentId,
                    psNamesInfo: new Map<string, TQFont>(),
                    fontIDsInfo: fontIdsArrayOfChunks[fontIdsIndex],
                },
                retryCount: 3,
            };
            const { responsebody: responsebody, err: error, callback: _callback } = await this._scheduler.scheduleTask(tqtypes.TaskType.COLLAB_AUTH_API, callback);
            const res = this._collabAPIResponseParser(responsebody, error, _callback);
            for (const [key, value] of res) {
                results.set(key, value);
            }
        }
        return results;
    }
}
